import { CommonModule, NgClass, NgFor } from '@angular/common';
import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { UiNotification } from '@techextensor/tab-core-utility';
import { TabSdk } from '@techextensor/tab-sdk';
import { Subject, takeUntil } from 'rxjs';
import { ContentPreviewPipe } from '../../../core/pipes/content-preview.pipe';

@Component({
    selector: 'app-notification',
    imports: [NgClass, NgFor, CommonModule, ContentPreviewPipe],
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss']
})
export class NotificationComponent {
  public notifications: UiNotification[] = [];
  public isLoading?: boolean;
  public isPopupVisible = true;

  public currentPage = 0;
  public totalPages = 0;
  public containerHeight: string = 'auto';

  private destroy$ = new Subject<void>();

  @ViewChild('popup') popup: ElementRef | undefined;
  @ViewChild('notificationContainer') notificationContainer: ElementRef | undefined;

  /**
   * Closes the popup if a click lands outside of it.
   */
  @HostListener('document:mousedown', ['$event'])
  public onOutsideClick(event: MouseEvent): void {
    if (this.isPopupVisible && this.popup?.nativeElement && !this.popup.nativeElement.contains(event.target)) {
      this.closeNotificationPopup();
    }
  }

  ngOnInit(): void {
    this.isLoading = true;

    // Latest notifications stream — array on page fetch, single item on live push
    TabSdk.notification.latestNotifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        if (Array.isArray(payload)) {
          this.notifications = [...this.notifications, ...payload];
        } else {
          this.notifications = [payload, ...this.notifications];
        }
      });

    // Read state — id ⇒ specific item read, null ⇒ all read
    TabSdk.notification.markAsReadEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notificationId => {
        if (notificationId === null) {
          this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
        } else {
          this.notifications = this.notifications.map(n =>
            n.id === notificationId ? { ...n, isRead: true } : n
          );
        }
      });

    // Cleared notifications — array of ids ⇒ specific items, null ⇒ all
    TabSdk.notification.clearEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notificationIds => {
        if (notificationIds === null) {
          this.notifications = [];
        } else {
          this.notifications = this.notifications.filter(n => !notificationIds.includes(n.id));
        }
      });

    TabSdk.notification.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });

    TabSdk.notification.totalPages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(totalPages => {
        this.totalPages = totalPages;
        if (this.currentPage === 1) {
          setTimeout(() => this.checkAndForceScroll(), 100);
        }
      });

    // Fetch the first page
    this.loadMoreNotifications();
  }

  /**
   * Force scroll only when: no natural scroll + more pages available
   */
  private checkAndForceScroll(): void {
    if (!this.notificationContainer?.nativeElement) return;

    const container = this.notificationContainer.nativeElement;
    const hasNaturalScroll = container.scrollHeight > container.clientHeight;
    const hasMorePages = this.totalPages > 1;

    if (!hasNaturalScroll && hasMorePages) {
      const currentHeight = container.scrollHeight;
      this.containerHeight = `${currentHeight - 20}px`;
    }
  }

  /**
   * Loads the next page of notifications from the inbox.
   */
  public loadMoreNotifications(): void {
    this.currentPage++;
    TabSdk.notification.loadMore(this.currentPage);
  }

  public handleMarkAllAsRead(): void {
    TabSdk.notification.markAllAsRead();
  }

  public markAsRead(notification: UiNotification): void {
    if (!notification?.isRead) {
      TabSdk.notification.markAsRead(notification.id);
    }
  }

  public handleRemoveAllNotifications(): void {
    TabSdk.notification.clearAll();
  }

  public removeNotification(notification: UiNotification): void {
    TabSdk.notification.clearOne(notification.id);
  }

  /**
   * Open the action URL of a notification (if any) in a new tab.
   * Marks the notification as read first.
   */
  public openNotification(notification: UiNotification): void {
    if (this.hasValidUrl(notification)) {
      this.markAsRead(notification);
      const actionUrl = notification.parsedPayload?.actions?.[0]?.url;
      const targetUrl = `${window.location.origin}/${TabSdk.store.appName}/${actionUrl}`;
      window.open(targetUrl, '_blank');
      this.closeNotificationPopup();
    }
  }

  public hasValidUrl(notification: UiNotification): boolean {
    return !!notification?.parsedPayload?.actions?.[0]?.url;
  }

  public closeNotificationPopup(): void {
    this.isPopupVisible = false;
  }

  /**
   * Infinite-scroll trigger — loads the next page when the user reaches the bottom.
   */
  public onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const atBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) <= 5;

    if (atBottom && !this.isLoading && this.currentPage < this.totalPages) {
      this.loadMoreNotifications();
    }
  }

  ngOnDestroy(): void {
    this.notifications = [];
    this.destroy$.next();
    this.destroy$.complete();
  }
}
